import { useCallback, useState } from "react";
import { t } from "ttag";

import { useDispatch, useSelector } from "metabase/lib/redux";

import { login } from "../../actions";
import {
  getExternalAuthProviders,
  getHasSessionCookies,
  getIsLdapEnabled,
} from "../../selectors";
import type { LoginData } from "../../types";
import { AuthButton } from "../AuthButton";
import { LoginForm } from "../LoginForm";
import { OTPVerification } from "../OTPVerification";

import { ActionList, ActionListItem } from "./PasswordPanel.styled";

interface PasswordPanelProps {
  redirectUrl?: string;
}

export const PasswordPanel = ({ redirectUrl }: PasswordPanelProps) => {
  const providers = useSelector(getExternalAuthProviders);
  const isLdapEnabled = useSelector(getIsLdapEnabled);
  const hasSessionCookies = useSelector(getHasSessionCookies);
  const dispatch = useDispatch();

  const [showOTPModal, setShowOTPModal] = useState(false);
  const [credentials, setCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (data: LoginData) => {
      setLoginError(null);
      try {
        const result = await dispatch(login({ data, redirectUrl }));

        // Check if login was successful
        if (login.fulfilled.match(result)) {
          // Login successful - refresh page
          window.location.reload();
          return;
        }

        // Check if OTP is required (returns rejected with special payload)
        if (login.rejected.match(result)) {
          const error = result.payload as any;
          if (error?.isOTPRequired) {
            // Store credentials for OTP verification
            setCredentials({
              username: data.username,
              password: data.password,
            });
            setShowOTPModal(true);
            return;
          }
          // Other errors - throw so form shows error state
          setLoginError(error?.message || t`Login failed`);
          throw new Error(error?.message || t`Login failed`);
        }
      } catch (err: any) {
        setLoginError(err?.message || t`An error occurred`);
        throw err;
      }
    },
    [dispatch, redirectUrl],
  );

  const handleOTPBack = useCallback(() => {
    setShowOTPModal(false);
    setCredentials(null);
    setLoginError(null);
  }, []);

  return (
    <div>
      <LoginForm
        isLdapEnabled={isLdapEnabled}
        hasSessionCookies={hasSessionCookies}
        onSubmit={handleSubmit}
        formError={loginError}
      />
      <ActionList>
        <ActionListItem>
          <AuthButton link="/auth/forgot_password">
            {t`I seem to have forgotten my password`}
          </AuthButton>
        </ActionListItem>
        {providers.map((provider) => (
          <ActionListItem key={provider.name}>
            <provider.Button redirectUrl={redirectUrl} />
          </ActionListItem>
        ))}
      </ActionList>
      {showOTPModal && credentials && (
        <OTPVerification
          username={credentials.username}
          password={credentials.password}
          onLoginSuccess={() => window.location.reload()}
          onBack={handleOTPBack}
        />
      )}
    </div>
  );
};
